import { useCallback, useEffect, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TaskCardType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import EToolTip from 'components/others/EToolTip';
import { useTask } from '../TaskContext';
import TaskCardContent, { type TaskCardOpenIntent } from './TaskCardContent';
import SolarIcon from 'components/icons/SolarIcon';

export type { TaskCardOpenIntent };

export interface TaskCardProps {
    /** ID задачі; `null` — офканвас закритий */
    taskId: number | null;
    onHide: () => void;
    /** Відкрити вкладку «Відповідь» або форму «Повернути» після завантаження */
    openIntent?: TaskCardOpenIntent;
    onOpenIntentApplied?: () => void;
}

const TaskCard = ({ taskId, onHide, openIntent = null, onOpenIntentApplied }: TaskCardProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const { triggerListRefresh } = useTask();
    const [task, setTask] = useState<TaskCardType | null>(null);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const fetchTask = useCallback(
        async (id: number, mode: 'load' | 'refresh' = 'load') => {
            if (mode === 'load') {
                setLoading(true);
                setTask(null);
            } else {
                setRefreshing(true);
            }

            const fetched = await apiTask.getTask(id);

            if (mode === 'load') setLoading(false);
            else setRefreshing(false);

            if (!fetched) {
                showToast(t('common:messError'), 'danger');
                if (mode === 'load') onHide();
                return null;
            }

            setTask(fetched);
            return fetched;
        },
        [onHide, showToast, t],
    );

    useEffect(() => {
        if (!taskId) {
            const timer = window.setTimeout(() => setTask(null), 300);
            return () => window.clearTimeout(timer);
        }

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setTask(null);
            const fetched = await apiTask.getTask(taskId);
            if (cancelled) return;
            setLoading(false);
            if (!fetched) {
                showToast(t('common:messError'), 'danger');
                onHide();
                return;
            }
            setTask(fetched);
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [taskId]);

    const handleTaskUpdated = (next: TaskCardType) => {
        setTask(next);
        triggerListRefresh();
    };

    const handleRefresh = async () => {
        if (!taskId || loading || refreshing) return;
        const fetched = await fetchTask(taskId, 'refresh');
        if (fetched) triggerListRefresh();
    };

    return (
        <Offcanvas
            show={taskId !== null}
            onHide={onHide}
            placement="end"
            backdrop
            backdropClassName="task-card-offcanvas-backdrop"
            className="task-card-offcanvas"
        >
            <Offcanvas.Header className="border-bottom">
                <Offcanvas.Title className="text-truncate pe-2">
                    {task?.taskName || t('titleCard')}
                </Offcanvas.Title>
                <div className="d-flex align-items-center flex-shrink-0 ms-auto">
                    <EToolTip title={t('common:toolRefreshList')}>
                        <Button
                            type="button"
                            size="sm"
                            variant="link"
                            className="text-secondary p-1"
                            disabled={!taskId || loading || refreshing}
                            aria-label={t('common:toolRefreshList')}
                            onClick={() => void handleRefresh()}
                        >
                            {refreshing ? (
                                <span className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                                <SolarIcon name="arrow-clockwise" className="fs-16" />
                            )}
                        </Button>
                    </EToolTip>
                    <button
                        type="button"
                        className="btn-close ms-0"
                        aria-label="Close"
                        onClick={onHide}
                    />
                </div>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {loading || !task ? (
                    <div className="py-5">
                        <ESpinner />
                    </div>
                ) : (
                    <TaskCardContent 
                        task={task}
                        onTaskUpdated={handleTaskUpdated}
                        onHide={onHide}
                        openIntent={openIntent}
                        onOpenIntentApplied={onOpenIntentApplied}
                    />
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default TaskCard;
